'use client';

import { ArrowRight, X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useState, useRef } from 'react';
import { NonBlockingDrawer } from '@/components/ui/non-blocking-drawer';
import { blogDrawerStorage } from '@/lib/blog-drawer-storage';
import { useAnalytics } from '@/services/frontendAnalyticsService';

interface BlogTrialDrawerMobileProps {
  postSlug: string;
  isAuthenticated: boolean;
}

export function BlogTrialDrawerMobile({ postSlug, isAuthenticated }: BlogTrialDrawerMobileProps) {
  const analytics = useAnalytics();
  const router = useRouter();
  const [showDrawer, setShowDrawer] = useState(false);
  const hasShownRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated || blogDrawerStorage.isCooldownActive()) return;

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const clientHeight = window.innerHeight;

      const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;

      if (scrollPercentage >= 25 && !hasShownRef.current) {
        setShowDrawer(true);
        hasShownRef.current = true;
        analytics.blog.drawer.shown({ postSlug, scrollDepth: scrollPercentage, device: 'mobile' });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAuthenticated, postSlug, analytics]);

  const handleClose = () => {
    setShowDrawer(false);
    blogDrawerStorage.setDismissed();
    analytics.blog.drawer.closed({ postSlug, device: 'mobile' });
  };

  const handleCTAClick = () => {
    analytics.blog.drawer.ctaClicked({ postSlug, device: 'mobile' });
    analytics.blog.featuresCTAClicked({
      postSlug,
      ctaType: 'drawer',
      ctaLabel: 'Mulai Trial Gratis',
    });
    blogDrawerStorage.setDismissed();
    router.push('/features');
  };

  if (isAuthenticated) return null;

  return (
    <NonBlockingDrawer open={showDrawer} className="max-h-[35vh]">
      <div className="relative p-4">
        <button
          onClick={handleClose}
          className="absolute right-3 top-3 p-1 rounded-sm opacity-70 hover:opacity-100"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-lg font-bold text-black uppercase tracking-tight pr-8">
          Coba Fitur Professional Gratis
        </h3>

        <p className="text-sm text-neutral-600 mt-1 line-clamp-2">
          Akses 24 jam konsolidasi, AI research, dan pemetaan relasi — tanpa kewajiban berlangganan.
        </p>

        <button
          onClick={handleCTAClick}
          className="w-full mt-3 bg-neutral-900 text-white px-4 py-2.5 font-bold text-sm border-2 border-black hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2"
        >
          Mulai Trial Gratis
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </NonBlockingDrawer>
  );
}
